<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from "vue";

import format from "@/services/Utils/format";
import { useTooltip } from "@stores/useTooltip";

import { HorizontalBarChart, type ChartTooltipEventType } from "@/features/Charts";
import { buildChartRowTooltipHtml } from "@/features/Charts/utils/chartTooltip";
import {
  useHotspotBreakdown,
  rowsToChartData,
  STAGE_BUCKETS,
  type HotspotGroupBy,
  type HotspotRow,
  type StageBucketKey,
} from "@/features/Dashboard/composables/useHotspotBreakdown";

import Switch from "@/components/Reusables/Switches/Switch.vue";
import SwitchTabs from "@/components/Reusables/Switches/SwitchTabs.vue";
import InfoIcon from "@/features/Tooltip/InfoIcon.vue";

const props = withDefaults(defineProps<{ groupBy?: HotspotGroupBy }>(), { groupBy: "material" });

const { getRows, emissionUnit, globalStageBuckets } = useHotspotBreakdown();
const tooltip = useTooltip();

// The grouping is fixed by whichever chart opened the modal.
const noun = computed(() => (props.groupBy === "type" ? "type" : "material"));
const nounPlural = computed(() => (props.groupBy === "type" ? "types" : "materials"));
const title = computed(() => `Highest Emission Impact by ${props.groupBy === "type" ? "Type" : "Material"}`);

const infoTooltip = computed(
  () =>
    `Every ${noun.value} in the project ranked by total emissions — not just the top 10. Each bar is split by ` +
    `life-cycle stage: A1–A3 production, A4 transport, A5 installation, B use, C end-of-life, D beyond the system ` +
    `boundary. Toggle stages with the chips, or turn off "Stage breakdown" to compare totals.`
);

// View controls (local state — the modal owns its own view, nothing leaks to the store).
const showStageBreakdown = ref(true);
// Relative mode normalizes every bar to 100% so stage composition stays legible when one stage
// (usually A1–A3) dominates. Only meaningful while the stage breakdown is shown.
const relativeView = ref(false);
// The chips are a sub-filter of the dashboard's global Stages filter, so they start at whatever that
// filter admits rather than at all six buckets. Offering a stage the global filter excluded was
// offering one the breakdown has no data for, which read as "you are looking at every stage" when you
// were not (RTLCA-5290).
const stageBuckets = computed(() => STAGE_BUCKETS.filter((bucket) => globalStageBuckets.value.includes(bucket.key)));
const activeStages = ref<StageBucketKey[]>([...globalStageBuckets.value]);

// The global filter bar stays reachable behind the modal, so it can change while this is open. Reset
// the local selection to the new scope when it does: keeping a stage the dashboard just filtered out
// would silently put this chart back out of step with the rest of the page. Keyed on the bucket list
// itself so an unrelated store change cannot throw away the user's chip selection.
const stopStageScopeSync = watch(
  () => globalStageBuckets.value.join(","),
  () => {
    activeStages.value = [...globalStageBuckets.value];
  }
);
onUnmounted(() => stopStageScopeSync());
const isRelative = computed(() => showStageBreakdown.value && relativeView.value);

// Maps a chart series' display name (bucket label) back to its bucket key so the tooltip can look
// up the absolute stage value regardless of whether the chart is showing absolute or relative bars.
const bucketByLabel = new Map(STAGE_BUCKETS.map((bucket) => [bucket.label, bucket.key]));

const toggleStage = (key: StageBucketKey) => {
  activeStages.value = activeStages.value.includes(key)
    ? activeStages.value.filter((stage) => stage !== key)
    : [...activeStages.value, key];
};

const rows = computed(() => getRows(props.groupBy));
const hasData = computed(() => rows.value.length > 0);
const noActiveStages = computed(() => showStageBreakdown.value && activeStages.value.length === 0);

// The total actually shown for a row: the sum of the currently-active stages (or the full total when
// the stage breakdown is off). Keeps the KPIs, tooltip and on-bar labels consistent as stages toggle.
const rowVisibleTotal = (row: HotspotRow): number =>
  showStageBreakdown.value ? activeStages.value.reduce((sum, key) => sum + row.stages[key], 0) : row.total;

// The row's total ignoring sign. This is the denominator behind a stage's share of its own row, both
// in the chart's relative bars and in the tooltip below, because the signed total can sit near zero
// on a row that mixes emissions with credits and turn a share into a meaningless 900% (RTLCA-5288).
const rowVisibleMagnitude = (row: HotspotRow): number =>
  showStageBreakdown.value ? activeStages.value.reduce((sum, key) => sum + Math.abs(row.stages[key]), 0) : Math.abs(row.total);

// The project's net emissions, shown as the headline KPI. Signed: a credit genuinely lowers it.
const grandTotal = computed(() => rows.value.reduce((sum, row) => sum + rowVisibleTotal(row), 0));

// A row's share of the project, measured against the sum of the rows' absolute totals for the same
// reason the bars are: a project whose credits offset much of its emissions has a small signed
// total, and "168% of all types" is not a share of anything. Note this sums ABSOLUTE ROW TOTALS,
// while rowVisibleMagnitude above sums a single row's absolute STAGES — the two answer different
// questions ("how big is this row in the project" vs "how much of this bar is this stage"), and
// mixing them would make the KPI and the on-bar label disagree about the same row.
const grandMagnitude = computed(() => rows.value.reduce((sum, row) => sum + Math.abs(rowVisibleTotal(row)), 0));
const share = (value: number): number => (grandMagnitude.value ? Math.round((Math.abs(value) / grandMagnitude.value) * 100) : 0);
const formatValue = (value: number): string => format.decimalNumber(value);

const subtitle = computed(() => `${rows.value.length} ${nounPlural.value}, sorted by total ${emissionUnit.value}`);
const topContributor = computed(() => {
  const top = rows.value[0];
  return top ? { label: top.label, share: share(rowVisibleTotal(top)) } : null;
});

const chartData = computed(() =>
  rowsToChartData(rows.value, {
    showStageBreakdown: showStageBreakdown.value,
    activeStages: activeStages.value,
    mode: isRelative.value ? "relative" : "absolute",
  })
);

// In relative mode the x-axis is a percentage scale; otherwise it's the emission unit.
const chartUnit = computed(() => (isRelative.value ? "%" : emissionUnit.value));

// Does any visible row carry a credit (a negative stage)? Only then does a relative bar extend left
// of zero, and only then is it worth spending half the plot on the negative side.
const hasNegativeStage = computed(() => rows.value.some((row) => activeStages.value.some((key) => row.stages[key] < 0)));

// "Deep dive" shows every entry: give each bar a fixed vertical band so spacing stays consistent
// whether there are 2 rows or 50 (and the region scrolls when there are many). Sizing to the row
// count — rather than a large fixed minimum — avoids ECharts stretching a handful of bars across a
// tall canvas, which leaves them thin and floating far apart. AXIS_CHROME covers the x-axis + label.
const ROW_HEIGHT = 44;
const AXIS_CHROME = 80;
const chartHeight = computed(() => rows.value.length * ROW_HEIGHT + AXIS_CHROME);

const chartSettings = computed(() => ({
  inverse: true,
  // The modal is wide, so spend the room on full names: at the default 120px budget two different
  // rows can both truncate to the same string ("Sub-layer for wood…").
  categoryLabelWidth: 220,
  // Own colour-coded legend/toggle row above handles stage on/off, so the ECharts legend stays off.
  legend: false,
  stacked: showStageBreakdown.value,
  // Every bar is 100% in relative mode, so the per-bar total/share label would be noise — hide it.
  valueLabel: isRelative.value ? ("none" as const) : ("total" as const),
  // Pin the relative axis so a bar's length means the same thing across rows and as stages are
  // toggled. Left it unpinned in absolute mode, where the axis should follow the emissions.
  ...(isRelative.value ? { axisMin: hasNegativeStage.value ? -100 : 0, axisMax: 100 } : {}),
}));

const onHover = (event: ChartTooltipEventType) => {
  const data = event?.data as { id?: string } | undefined;
  const row = data?.id ? rows.value.find((candidate) => candidate.id === data.id) : undefined;
  const rowTotal = row ? rowVisibleTotal(row) : 0;
  const unit = emissionUnit.value;

  // Row hover: the pointer is in the row's band or on its name, not on a stage, so show what the row
  // is made of. Built from this row's own stage values rather than the event's, for the same reason
  // the stage line below is — in relative mode the chart is fed percentages, and the tooltip should
  // always report the real figures.
  // `seriesName` set means the pointer is on a stage's ink, and the stage line below answers that;
  // the row-band event deliberately leaves it unset (segment events carry rowBreakdown too now).
  if (event?.rowBreakdown && !event?.seriesName && row && showStageBreakdown.value) {
    tooltip.showTooltip(
      buildChartRowTooltipHtml({
        label: String(event.name ?? ""),
        subtitle: row.model,
        segments: STAGE_BUCKETS.filter((bucket) => activeStages.value.includes(bucket.key)).map((bucket) => ({
          name: bucket.label,
          value: row.stages[bucket.key],
          color: bucket.color,
        })),
        total: rowTotal,
        unit,
        formatValue,
        footnote: `${share(rowTotal)}% of all ${nounPlural.value}`,
      })
    );
    return;
  }

  // Always report the absolute stage value + its share of the item, whether the chart is showing
  // absolute or relative bars — event.value would be a percentage in relative mode.
  const bucketKey = event?.seriesName ? bucketByLabel.get(String(event.seriesName)) : undefined;
  const stageValue = row && bucketKey ? row.stages[bucketKey] : 0;
  // Share of the row's own magnitude, so this number is the one the bar segment is drawn at.
  const rowMagnitude = row ? rowVisibleMagnitude(row) : 0;
  const stageLine =
    showStageBreakdown.value && event?.seriesName
      ? `<div class="mb-px-6 h-px-8 w-px-24 rounded-4" style="background-color: ${event?.color || "transparent"}"></div>
         <span class="t-caption">${event.seriesName}: ${formatValue(stageValue)} ${unit}${
           rowMagnitude ? ` (${Math.round((stageValue / rowMagnitude) * 100)}% of item)` : ""
         }</span>`
      : "";

  tooltip.showTooltip(`
    <div class="flex flex-col text-white">
      <span class="t-caption-bold">${event?.name ?? ""}</span>
      ${row?.model ? `<span class="t-footnote opacity-80">${row.model}</span>` : ""}
      ${stageLine}
      <span class="t-caption">Total: ${formatValue(rowTotal)} ${unit}</span>
      <span class="t-footnote opacity-80">${share(rowTotal)}% of all ${nounPlural.value}</span>
    </div>
  `);
};

const onLeave = () => tooltip.hideTooltip();
</script>

<template>
  <!-- Keeps the page's slate-0 inside the modal panel, so the toggle and stage chips stay a step below it. -->
  <div class="gap-px-16 flex h-full flex-col dark:[--color-slate-0:var(--modal-band)]">
    <div class="gap-px-16 flex flex-wrap items-start justify-between">
      <div class="flex flex-col">
        <div class="gap-px-6 flex items-center">
          <h2 class="t-sh3">{{ title }}</h2>
          <InfoIcon :tooltip="infoTooltip" />
        </div>
        <span class="t-caption text-type-muted">{{ subtitle }}</span>
      </div>

      <div class="gap-px-16 flex flex-wrap items-center">
        <SwitchTabs
          v-if="showStageBreakdown"
          :value="relativeView"
          size="fit"
          @update:value="relativeView = $event">
          <template #left>Absolute</template>
          <template #right>Relative</template>
        </SwitchTabs>

        <Switch
          :value="showStageBreakdown"
          color="primary"
          label="Stage breakdown"
          @update:value="showStageBreakdown = $event" />
      </div>
    </div>

    <div
      v-if="hasData"
      class="gap-px-24 rounded-8 p-px-12 flex flex-wrap bg-slate-100">
      <div class="flex min-w-0 flex-col">
        <span class="t-footnote text-type-muted tracking-wide uppercase">Total emissions</span>
        <span class="t-sh4"
          >{{ formatValue(grandTotal) }} <span class="t-caption text-type-muted">{{ emissionUnit }}</span></span
        >
      </div>
      <div class="flex flex-col">
        <span class="t-footnote text-type-muted tracking-wide uppercase">{{
          props.groupBy === "type" ? "Types" : "Materials"
        }}</span>
        <span class="t-sh4">{{ rows.length }}</span>
      </div>
      <div
        v-if="topContributor"
        class="flex min-w-0 flex-col">
        <span class="t-footnote text-type-muted tracking-wide uppercase">Top contributor</span>
        <span class="t-sh4 truncate"
          >{{ topContributor.label }} <span class="t-caption text-type-muted">{{ topContributor.share }}%</span></span
        >
      </div>
    </div>

    <div
      v-if="showStageBreakdown"
      class="gap-px-8 flex flex-wrap items-center"
      role="group"
      aria-label="Toggle life-cycle stages">
      <button
        v-for="bucket in stageBuckets"
        :key="bucket.key"
        type="button"
        class="focus-ring interactive-active gap-px-6 px-px-8 py-px-4 rounded-4 duration-fast ease-standard flex items-center border transition-colors"
        :class="
          activeStages.includes(bucket.key)
            ? 'bg-slate-0 text-type-default border-slate-300'
            : 'text-type-muted border-slate-200 bg-slate-100 opacity-60'
        "
        :aria-pressed="activeStages.includes(bucket.key)"
        @click="toggleStage(bucket.key)">
        <!-- style kept: stage swatch colour is a data-driven hex that must match the chart series exactly -->
        <span
          class="h-px-10 w-px-10 rounded-full"
          :style="{ backgroundColor: bucket.color }" />
        <span class="t-caption">{{ bucket.label }}</span>
      </button>
    </div>

    <div class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
      <p
        v-if="!hasData"
        class="t-body text-type-muted flex h-full items-center justify-center">
        No emission data available for this project.
      </p>
      <p
        v-else-if="noActiveStages"
        class="t-body text-type-muted flex h-full items-center justify-center">
        Select at least one life-cycle stage to display.
      </p>
      <!-- style kept: chart height scales with the row count so every entry keeps a readable bar; not expressible with static Tailwind -->
      <div
        v-else
        :style="{ height: `${chartHeight}px` }">
        <HorizontalBarChart
          :data="chartData"
          :unit="chartUnit"
          :settings="chartSettings"
          :on-hover="onHover"
          :on-leave="onLeave" />
      </div>
    </div>
  </div>
</template>
